import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { useTheme } from "../../../../context/ThemeContext";
import { buildThemeClasses } from "../../theme";
import { fetchEconomics, isForbidden, saveStageKinds, type BucketAmounts, type EconomicsReport, type StageClass } from "../../services/economicsService";
import { ActivityChart, AllocationBar, BucketLegend, BucketSplitBar, Exhibit, FunnelChart, MonthlySpendChart, PipelineMixBar, StageDurationChart, StageOutcomeChart, UnitCostTrend } from "./EconomicsCharts";
import { BUCKET_META, BUCKET_ORDER, OUTCOME_META, QUALITY_TONES, SEVERITY_TONES, STAGE_CLASS_OPTIONS, days, fileSize, money, monthLabel, num, pct, presetRange, relativeTime, titles, type RangePreset } from "./economicsFormat";

// ============================================================================
// Panel CRM → Dashboard: economía del embudo (#1291, v2 #1352).
//
// Se lee como un informe de consultoría: la respuesta primero (resumen
// ejecutivo), después los indicadores, y luego la evidencia en exhibits
// numerados cuyo título ES la conclusión. Todos los totales de costo llevan su
// desglose por bolsa (publicidad, plataformas, equipo de marketing, equipo de
// ventas). Lo calcula el backend; aquí solo se pinta.
// ============================================================================

const PRESETS: { id: Exclude<RangePreset, "custom">; label: string }[] = [
  { id: "3m", label: "3 meses" },
  { id: "6m", label: "6 meses" },
  { id: "12m", label: "12 meses" },
  { id: "ytd", label: "Año en curso" },
];

type TC = ReturnType<typeof buildThemeClasses>;

export default function EconomicsDashboard() {
  const { isDarkMode } = useTheme();
  const tc = buildThemeClasses(isDarkMode);
  const [preset, setPreset] = useState<RangePreset>("12m");
  const [range, setRange] = useState(() => presetRange("12m"));
  const [pipeline, setPipeline] = useState<string | null>(null);
  const [report, setReport] = useState<EconomicsReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Cada petición lleva su número: si el usuario cambia el periodo mientras una
  // lenta sigue en vuelo, la respuesta vieja llega después y no debe pintarse.
  const requestSeq = useRef(0);

  const load = useCallback(
    async (refresh = false) => {
      const seq = ++requestSeq.current;
      if (refresh) setRefreshing(true);
      else setLoading(true);
      setError(null);
      try {
        const data = await fetchEconomics({ from: range.from, to: range.to, pipeline, refresh });
        if (seq !== requestSeq.current) return;
        setReport(data);
        setForbidden(false);
      } catch (err) {
        if (seq !== requestSeq.current) return;
        if (isForbidden(err)) setForbidden(true);
        else setError((err as Error)?.message || "No se pudo cargar el análisis");
      } finally {
        if (seq === requestSeq.current) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    },
    [range.from, range.to, pipeline],
  );

  useEffect(() => {
    void load(false);
  }, [load]);

  const pickPreset = (id: Exclude<RangePreset, "custom">) => {
    setPreset(id);
    setRange(presetRange(id));
  };
  const setCustom = (key: "from" | "to", value: string) => {
    if (!/^\d{4}-\d{2}$/.test(value)) return;
    setPreset("custom");
    setRange((r) => ({ ...r, [key]: value }));
  };
  const [savingStage, setSavingStage] = useState<string | null>(null);
  const changeStage = async (stageId: string, kind: StageClass | null) => {
    if (!stageId || savingStage) return;
    setSavingStage(stageId);
    try {
      await saveStageKinds({ [stageId]: kind });
      await load(false);
    } catch (err) {
      setError((err as Error)?.message || "No se pudo guardar la definición de la etapa");
    } finally {
      setSavingStage(null);
    }
  };

  const t = useMemo(() => (report ? titles(report) : null), [report]);

  return (
    <div className="h-full w-full min-w-0 overflow-y-auto p-4 sm:p-6">
      <div className="mx-auto max-w-7xl space-y-6">
        <Header tc={tc} report={report} preset={preset} range={range} pipeline={pipeline} onPreset={pickPreset} onCustom={setCustom} onPipeline={setPipeline} onRefresh={() => load(true)} refreshing={refreshing} />

        {forbidden && <Locked tc={tc} />}
        {error && !forbidden && (
          <p role="alert" className="rounded-lg bg-red-500/10 p-3 text-sm text-red-500">
            {error}
          </p>
        )}
        {loading && !report && !forbidden && <Skeleton tc={tc} />}

        {report && t && !forbidden && (
          <>
            <ExecutiveSummary report={report} />
            <Scorecard report={report} tc={tc} />

            <Exhibit n={1} kicker="Inversión comercial" title={t.investment} testId="exhibit-investment" source="Fuente: Gastos (categoría marketing, plataformas de marketing y comisiones, sin gastos personales), comprobantes de nómina y padrón. Equipo = lo pagado a cada persona; donde no hay pago registrado, estimación del padrón.">
              <div className="grid grid-cols-1 gap-6 xl:grid-cols-5">
                <div className="xl:col-span-3">
                  <MonthlySpendChart monthly={report.costs.monthly} />
                </div>
                <div className="xl:col-span-2">
                  <BucketTable report={report} tc={tc} />
                </div>
              </div>
            </Exhibit>

            <Exhibit n={2} kicker="Costo unitario por bolsa" title={t.unitCosts} testId="exhibit-unit-costs" source="Cada costo unitario = inversión del periodo ÷ unidades. El desglose reparte ese costo entre las bolsas en la misma proporción que el total.">
              <UnitCostMatrix report={report} tc={tc} />
            </Exhibit>

            <Exhibit n={3} kicker={`Embudo · ${report.funnel.pipelineLabel}`} title={t.funnel} testId="exhibit-funnel" source={`Fuente: HubSpot (contactos, negocios y su recorrido de etapas) y formularios propios. «Llegaron» = negocios cuya etapa más avanzada es esa o una posterior. Recorrido real en ${num(report.funnel.exactHistory)} de ${num(report.kpis.deals)} negocios.${report.funnel.mode === "all" && report.funnel.pipelines.length > 1 ? " Con varios pipelines, sus etapas se agrupan en fases estándar." : ""}`}>
              <div className="grid grid-cols-1 gap-6 xl:grid-cols-5">
                <div className="xl:col-span-2">
                  <FunnelChart stages={report.funnel.stages} />
                </div>
                <div className="overflow-x-auto xl:col-span-3">
                  <FunnelTable report={report} tc={tc} />
                </div>
              </div>
            </Exhibit>

            <Exhibit n={4} kicker="Fugas: dónde se queda el dinero" title={t.leakage} testId="exhibit-leakage" source="Reparto de la inversión: costo por negocio × negocios de cada resultado. La gráfica muestra, para cada etapa, cuántos negocios siguen activos ahí y cuántos se perdieron o quedaron en pausa sin pasar de ella.">
              <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
                <AllocationBar allocation={report.allocation} />
                <StageOutcomeChart stages={report.funnel.stages} />
              </div>
            </Exhibit>

            <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
              <Exhibit n={5} kicker="Actividad comercial" title={t.activity} className="xl:col-span-2" testId="exhibit-activity" source="Fuente: reuniones de HubSpot. Demo = reunión registrada (no cancelada). Izquierda: demos por mes. Derecha: costo por lead y por demo de cada mes.">
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                  <ActivityChart monthly={report.activity.monthly} />
                  <UnitCostTrend cohorts={report.cohorts} />
                </div>
              </Exhibit>
              <Exhibit n={6} kicker="Velocidad" title="Tiempo que un negocio pasa en cada etapa" testId="exhibit-velocity" source="Mediana de días entre que entra y sale de la etapa (solo intervalos cerrados).">
                <StageDurationChart stages={report.funnel.stages} />
              </Exhibit>
            </div>

            <Exhibit n={7} kicker="Cohortes por mes de creación" title={t.cohorts} testId="exhibit-cohorts" source="Leads y negocios por su mes de creación (fecha real de HubSpot, zona horaria de Ciudad de México). El costo del mes va desglosado por bolsa en la barra.">
              <CohortTable report={report} tc={tc} />
            </Exhibit>

            <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
              {report.pipelines.length > 1 && (
                <Exhibit n={8} kicker="Portafolio de pipelines" title={t.pipelines} testId="exhibit-pipelines" source="«Sin contactar» = negocios activos que no han salido de la primera etapa.">
                  <PipelineTable report={report} tc={tc} />
                </Exhibit>
              )}
              <Exhibit n={report.pipelines.length > 1 ? 9 : 8} kicker="Punto de equilibrio comercial" title={t.breakEven} testId="exhibit-breakeven" source="Costo comercial mensual = inversión del periodo ÷ meses con gasto. Ticket promedio = monto medio de los negocios del periodo que tienen monto capturado.">
                <BreakEven report={report} tc={tc} />
              </Exhibit>
            </div>

            <ActionPlan report={report} tc={tc} />
            <DataQuality report={report} tc={tc} />
            <LeadSources report={report} tc={tc} />
            <DealsSection report={report} tc={tc} />
            <DocumentsSection report={report} tc={tc} />
            <StageDefinitions report={report} tc={tc} onChange={changeStage} saving={savingStage} />
            <Methodology report={report} tc={tc} />
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cabecera
// ---------------------------------------------------------------------------

function Header({
  tc,
  report,
  preset,
  range,
  pipeline,
  onPreset,
  onCustom,
  onPipeline,
  onRefresh,
  refreshing,
}: {
  tc: TC;
  report: EconomicsReport | null;
  preset: RangePreset;
  range: { from: string; to: string };
  pipeline: string | null;
  onPreset: (p: Exclude<RangePreset, "custom">) => void;
  onCustom: (k: "from" | "to", v: string) => void;
  onPipeline: (id: string | null) => void;
  onRefresh: () => void;
  refreshing: boolean;
}) {
  const { isDarkMode } = useTheme();
  const input = `rounded-lg border px-2 py-1 text-xs ${isDarkMode ? "bg-[#232323] border-dark-border text-white" : "bg-white border-gray-300 text-gray-900"}`;
  const hs = report?.hubspot;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className={`text-[11px] font-semibold uppercase tracking-wider ${tc.textSecondary}`}>Panel CRM · Informe ejecutivo</p>
          <h1 className={`text-2xl font-bold ${tc.text}`}>Economía del embudo</h1>
          <p className={`text-sm ${tc.textSecondary}`}>Cuánto cuesta conseguir un cliente nuevo y en qué parte del camino se queda el dinero.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className={`inline-flex rounded-lg border p-0.5 text-xs ${tc.border}`} role="group" aria-label="Periodo">
            {PRESETS.map((p) => (
              <button key={p.id} type="button" aria-pressed={preset === p.id} onClick={() => onPreset(p.id)} className={`rounded-md px-2 py-1 ${preset === p.id ? "bg-primary-500 text-white" : tc.buttonText}`}>
                {p.label}
              </button>
            ))}
          </div>
          <label className={`flex items-center gap-1 text-xs ${tc.textSecondary}`}>
            De
            <input type="month" aria-label="Desde" className={input} value={range.from} max={range.to} onChange={(e) => onCustom("from", e.target.value)} />
          </label>
          <label className={`flex items-center gap-1 text-xs ${tc.textSecondary}`}>
            a
            <input type="month" aria-label="Hasta" className={input} value={range.to} min={range.from} onChange={(e) => onCustom("to", e.target.value)} />
          </label>
          {report && report.funnel.pipelines.length > 1 && (
            <select aria-label="Pipeline" className={input} value={pipeline ?? "all"} onChange={(e) => onPipeline(e.target.value === "all" ? null : e.target.value)}>
              <option value="all">Todos los pipelines</option>
              {report.funnel.pipelines.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label} ({p.deals})
                </option>
              ))}
            </select>
          )}
          <button type="button" onClick={onRefresh} disabled={refreshing} className="rounded-lg bg-primary-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-primary-600 disabled:opacity-50">
            <i className={`ri-refresh-line mr-1 ${refreshing ? "animate-spin" : ""}`} />
            {refreshing ? "Leyendo HubSpot…" : "Actualizar HubSpot"}
          </button>
        </div>
      </div>
      {report && (
        <div className="flex flex-wrap gap-2 text-xs">
          <Chip tone={hs?.connected ? "ok" : "warn"} icon={hs?.connected ? "ri-plug-line" : "ri-plug-2-line"}>
            {hs?.connected ? `HubSpot conectado · datos ${relativeTime(hs.cachedAt)}` : "HubSpot sin conectar: embudo con etapas locales"}
          </Chip>
          <Chip tone="info" icon="ri-git-branch-line">
            {report.funnel.pipelineLabel}
          </Chip>
          <Chip tone="muted" icon="ri-calendar-line">
            {monthLabel(report.period.from)} → {monthLabel(report.period.to)} · {report.currency}
          </Chip>
          {report.installedBase.count > 0 && (
            <Chip tone="muted" icon="ri-building-line">
              {num(report.installedBase.count)} clientes existentes fuera del embudo
            </Chip>
          )}
        </div>
      )}
    </div>
  );
}

const CHIP_TONES = {
  ok: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
  warn: "bg-amber-500/15 text-amber-700 dark:text-amber-400",
  info: "bg-sky-500/15 text-sky-600 dark:text-sky-400",
  muted: "bg-gray-400/15 text-gray-500",
};

function Chip({ tone, icon, children }: { tone: keyof typeof CHIP_TONES; icon: string; children: ReactNode }) {
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 ${CHIP_TONES[tone]}`}>
      <i className={icon} />
      {children}
    </span>
  );
}

function Locked({ tc }: { tc: TC }) {
  return (
    <div className={`rounded-2xl border p-8 text-center ${tc.cardBorder} ${tc.cardBg}`} data-testid="economics-locked">
      <i className="ri-lock-2-line text-3xl text-amber-500" />
      <h2 className={`mt-2 text-lg font-bold ${tc.text}`}>Solo para quien administra el dinero del equipo</h2>
      <p className={`mx-auto mt-1 max-w-xl text-sm ${tc.textSecondary}`}>
        Este tablero cruza gastos de marketing y los pagos del área comercial con los leads y negocios. Lo ven las mismas personas que ven la nómina en Gestión de Staff.
      </p>
    </div>
  );
}

function Skeleton({ tc }: { tc: TC }) {
  return (
    <div className="space-y-4" aria-busy="true" aria-label="Cargando análisis">
      <div className={`h-40 animate-pulse rounded-2xl border ${tc.cardBorder} ${tc.cardBg}`} />
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        {Array.from({ length: 10 }).map((_, i) => (
          <div key={i} className={`h-24 animate-pulse rounded-2xl border ${tc.cardBorder} ${tc.cardBg}`} />
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Resumen ejecutivo: la respuesta primero
// ---------------------------------------------------------------------------

function ExecutiveSummary({ report }: { report: EconomicsReport }) {
  const s = report.summary;
  return (
    <section className="rounded-2xl bg-slate-900 p-6 text-white shadow-sm dark:bg-slate-800" data-testid="economics-summary">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-300">Resumen ejecutivo</p>
      <h2 className="mt-1 text-xl font-bold leading-snug sm:text-2xl">{s.headline}</h2>
      <div className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-3">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-300">Situación</p>
          <p className="mt-1 text-sm leading-relaxed text-slate-100">{s.situation}</p>
        </div>
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-300">Complicación</p>
          <p className="mt-1 text-sm leading-relaxed text-slate-100">{s.complication}</p>
        </div>
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-300">Qué hacer</p>
          {s.resolution.length ? (
            <ol className="mt-1 list-decimal space-y-1 pl-4 text-sm leading-relaxed text-slate-100">
              {s.resolution.map((a) => (
                <li key={a}>{a}</li>
              ))}
            </ol>
          ) : (
            <p className="mt-1 text-sm text-slate-100">Sin acciones prioritarias con los datos actuales.</p>
          )}
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Scorecard
// ---------------------------------------------------------------------------

function Scorecard({ report, tc }: { report: EconomicsReport; tc: TC }) {
  const k = report.kpis;
  const unit = (id: string) => report.unitCosts.find((u) => u.id === id)?.byBucket ?? null;
  const volume: { label: string; value: string; hint: string }[] = [
    { label: "Leads (personas)", value: num(k.leads), hint: report.leads.bulk ? `${num(k.organicLeads)} sin la carga masiva` : `${num(report.leads.fromContacts)} de HubSpot · ${num(report.leads.fromForms)} de formularios` },
    { label: "Demos realizadas", value: num(k.demos), hint: `${num(report.activity.notes)} notas de seguimiento` },
    { label: "Negocios creados", value: num(k.deals), hint: `${pct(k.leadToDeal)} de los leads` },
    { label: "Propuestas enviadas", value: k.proposals == null ? "—" : num(k.proposals), hint: k.dealToProposal == null ? "sin etapa de propuesta" : `${pct(k.dealToProposal)} de los negocios` },
    { label: "Clientes nuevos", value: num(k.won), hint: `${num(k.active)} activos · ${num(k.parked)} en pausa · ${num(k.lost)} perdidos` },
  ];
  const cost: { label: string; value: string; hint: string; split: BucketAmounts | null }[] = [
    { label: "Inversión comercial", value: money(k.totalCost), hint: `${money(k.monthlyAverageCost)} al mes`, split: report.costs.byBucket },
    { label: "Costo por lead", value: money(k.costPerLead), hint: k.costPerOrganicLead != null ? `${money(k.costPerOrganicLead)} sin la carga masiva` : "inversión ÷ leads", split: unit("lead") },
    { label: "Costo por demo", value: money(k.costPerDemo), hint: "inversión ÷ demos", split: unit("demo") },
    { label: "Costo por negocio", value: money(k.costPerDeal), hint: k.costPerProposal != null ? `${money(k.costPerProposal)} por propuesta` : "inversión ÷ negocios", split: unit("deal") },
    { label: "Costo por cliente nuevo (CAC)", value: money(k.cac), hint: k.won ? `${num(k.won)} ganados` : "aún sin cierres", split: unit("win") },
  ];
  return (
    <section className="space-y-3" data-testid="economics-kpis">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        {volume.map((t) => (
          <div key={t.label} className={`rounded-2xl border p-4 ${tc.cardBorder} ${tc.cardBg}`}>
            <p className={`text-xs ${tc.textSecondary}`}>{t.label}</p>
            <p className={`mt-1 text-2xl font-bold tabular-nums ${tc.text}`}>{t.value}</p>
            <p className={`mt-0.5 text-xs ${tc.textSecondary}`}>{t.hint}</p>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        {cost.map((t) => (
          <div key={t.label} className={`rounded-2xl border p-4 ${tc.cardBorder} ${tc.cardBg}`}>
            <p className={`text-xs ${tc.textSecondary}`}>{t.label}</p>
            <p className={`mt-1 text-2xl font-bold tabular-nums ${tc.text}`}>{t.value}</p>
            <p className={`mb-2 mt-0.5 text-xs ${tc.textSecondary}`}>{t.hint}</p>
            <BucketSplitBar amounts={t.split} label={t.label} />
          </div>
        ))}
      </div>
      <BucketLegend amounts={report.costs.byBucket} total={report.costs.total} />
    </section>
  );
}

// ---------------------------------------------------------------------------
// Exhibit 1 y 2: inversión y costo unitario por bolsa
// ---------------------------------------------------------------------------

const LINE_KIND: Record<string, string> = { paid: "pagado", estimate: "estimado del padrón", expense: "", commission: "" };

function BucketTable({ report, tc }: { report: EconomicsReport; tc: TC }) {
  return (
    <table className="w-full text-xs" data-testid="economics-buckets">
      <thead>
        <tr className={`text-left ${tc.textSecondary}`}>
          <th className="py-1.5 pr-2 font-medium">Bolsa</th>
          <th className="py-1.5 pr-2 text-right font-medium">Acumulado</th>
          <th className="py-1.5 text-right font-medium">% del total</th>
        </tr>
      </thead>
      <tbody>
        {report.costs.buckets
          .filter((b) => b.amount > 0 || b.id !== "otherMarketing")
          .map((b) => (
            <tr key={b.id} className={`border-t align-top ${tc.border}`}>
              <td className="py-2 pr-2">
                <span className="flex items-center gap-1.5">
                  <span className="inline-block h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: BUCKET_META[b.id].color }} />
                  <span className={`font-medium ${tc.text}`}>{b.label}</span>
                </span>
                <p className={`mt-0.5 pl-4 ${tc.textSecondary}`}>
                  {b.lines.length
                    ? b.lines
                        .slice(0, 4)
                        .map((l) => `${l.name} ${money(l.amount)}${LINE_KIND[l.kind] ? ` (${LINE_KIND[l.kind]})` : ""}`)
                        .join(" · ")
                    : b.hint}
                </p>
              </td>
              <td className={`py-2 pr-2 text-right font-semibold tabular-nums ${tc.text}`}>{money(b.amount)}</td>
              <td className={`py-2 text-right tabular-nums ${tc.textSecondary}`}>{pct(b.share)}</td>
            </tr>
          ))}
        <tr className={`border-t-2 ${tc.border}`}>
          <td className={`py-2 pr-2 font-bold ${tc.text}`}>Inversión comercial</td>
          <td className={`py-2 pr-2 text-right font-bold tabular-nums ${tc.text}`}>{money(report.costs.total)}</td>
          <td className={`py-2 text-right tabular-nums ${tc.textSecondary}`}>100 %</td>
        </tr>
      </tbody>
    </table>
  );
}

function UnitCostMatrix({ report, tc }: { report: EconomicsReport; tc: TC }) {
  const used = BUCKET_ORDER.filter((id) => report.costs.byBucket[id] > 0 || id !== "otherMarketing");
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs" data-testid="economics-unit-costs">
        <thead>
          <tr className={`text-left ${tc.textSecondary}`}>
            <th className="py-2 pr-2 font-medium">Costo</th>
            <th className="py-2 pr-2 text-right font-medium">Unidades</th>
            {used.map((id) => (
              <th key={id} className="py-2 pr-2 text-right font-medium">
                <span className="inline-flex items-center gap-1">
                  <span className="inline-block h-2 w-2 rounded-sm" style={{ background: BUCKET_META[id].color }} />
                  {BUCKET_META[id].short}
                </span>
              </th>
            ))}
            <th className="py-2 pr-2 text-right font-medium">Total</th>
            <th className="w-40 py-2 font-medium">Reparto</th>
          </tr>
        </thead>
        <tbody>
          <tr className={`border-t ${tc.border}`}>
            <td className={`py-2 pr-2 font-medium ${tc.text}`}>Acumulado del periodo</td>
            <td className={`py-2 pr-2 text-right ${tc.textSecondary}`}>—</td>
            {used.map((id) => (
              <td key={id} className={`py-2 pr-2 text-right tabular-nums ${tc.text}`}>
                {money(report.costs.byBucket[id])}
              </td>
            ))}
            <td className={`py-2 pr-2 text-right font-bold tabular-nums ${tc.text}`}>{money(report.costs.total)}</td>
            <td className="py-2">
              <BucketSplitBar amounts={report.costs.byBucket} label="Acumulado" />
            </td>
          </tr>
          {report.unitCosts.map((u) => (
            <tr key={u.id} className={`border-t ${tc.border}`}>
              <td className={`py-2 pr-2 font-medium ${tc.text}`}>{u.label}</td>
              <td className={`py-2 pr-2 text-right tabular-nums ${tc.textSecondary}`}>{num(u.units)}</td>
              {used.map((id) => (
                <td key={id} className={`py-2 pr-2 text-right tabular-nums ${tc.textSecondary}`}>
                  {u.byBucket ? money(u.byBucket[id]) : "—"}
                </td>
              ))}
              <td className={`py-2 pr-2 text-right font-bold tabular-nums ${tc.text}`}>{money(u.total)}</td>
              <td className="py-2">
                <BucketSplitBar amounts={u.byBucket} label={u.label} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Exhibit 3: embudo
// ---------------------------------------------------------------------------

function FunnelTable({ report, tc }: { report: EconomicsReport; tc: TC }) {
  const { isDarkMode } = useTheme();
  return (
    <table className="w-full text-xs" data-testid="economics-funnel-table">
      <thead>
        <tr className={`text-left ${tc.textSecondary}`}>
          <th className="py-2 pr-2 font-medium">Etapa</th>
          <th className="py-2 pr-2 text-right font-medium">Llegaron</th>
          <th className="py-2 pr-2 text-right font-medium">Conversión</th>
          <th className="py-2 pr-2 text-right font-medium">Costo / unidad</th>
          <th className="w-28 py-2 pr-2 font-medium">Por bolsa</th>
          <th className="py-2 pr-2 text-right font-medium">P(cierre)</th>
          <th className="py-2 pr-2 text-right font-medium">Costo esperado de cierre</th>
          <th className="py-2 text-right font-medium">Mediana</th>
        </tr>
      </thead>
      <tbody>
        {report.funnel.stages.map((s) => (
          <tr key={s.id} className={`border-t ${tc.border} ${isDarkMode ? "hover:bg-white/5" : "hover:bg-gray-50"}`}>
            <td className={`py-2 pr-2 font-medium ${s.kind === "won" ? "text-emerald-600 dark:text-emerald-400" : tc.text}`}>
              {s.label}
              {s.kind === "open" && s.current != null && s.current > 0 && <span className={`ml-1 font-normal ${tc.textSecondary}`}>({num(s.current)} ahí)</span>}
            </td>
            <td className={`py-2 pr-2 text-right tabular-nums ${tc.text}`}>{num(s.reached)}</td>
            <td className={`py-2 pr-2 text-right tabular-nums ${tc.textSecondary}`}>{pct(s.conversionFromPrev)}</td>
            <td className={`py-2 pr-2 text-right tabular-nums ${tc.text}`}>{money(s.costPerUnit)}</td>
            <td className="py-2 pr-2">
              <BucketSplitBar amounts={s.costByBucket} label={`${s.label}, costo por unidad`} height={6} />
            </td>
            <td className={`py-2 pr-2 text-right tabular-nums ${tc.text}`} title={s.pHubspot != null ? `HubSpot: ${pct(s.pHubspot)}` : undefined}>
              {s.kind === "won" ? "—" : s.kind === "open" && s.decided != null && s.decided < 3 && s.pHubspot != null ? `${pct(s.pHubspot)}*` : pct(s.pClose)}
            </td>
            <td className={`py-2 pr-2 text-right font-semibold tabular-nums ${tc.text}`}>{money(s.expectedCloseCost)}</td>
            <td className={`py-2 text-right tabular-nums ${tc.textSecondary}`}>{days(s.medianDays)}</td>
          </tr>
        ))}
        <tr className={`border-t ${tc.border}`}>
          <td colSpan={8} className={`pt-2 text-[11px] ${tc.textSecondary}`}>
            Fuera del embudo activo: {num(report.funnel.outcomes.parked.deals)} en pausa · {num(report.funnel.outcomes.lost.deals)} perdidos. * Con menos de 3 negocios decididos se muestra la probabilidad configurada en HubSpot.
          </td>
        </tr>
      </tbody>
    </table>
  );
}

// ---------------------------------------------------------------------------
// Cohortes, pipelines, punto de equilibrio
// ---------------------------------------------------------------------------

/** Sombreado de una celda de conteo: un solo tono, más oscuro = más. */
function heat(value: number, max: number, isDarkMode: boolean): CSSProperties {
  if (!max || !value) return {};
  const a = 0.08 + 0.42 * (value / max);
  return { background: isDarkMode ? `rgba(122,162,214,${a})` : `rgba(30,58,95,${a})` };
}

function CohortTable({ report, tc }: { report: EconomicsReport; tc: TC }) {
  const { isDarkMode } = useTheme();
  const rows = report.cohorts;
  const max = (k: "leads" | "demos" | "deals" | "lost") => Math.max(0, ...rows.map((r) => r[k]));
  const maxProposals = Math.max(0, ...rows.map((r) => r.proposals ?? 0));
  if (!rows.some((r) => r.cost || r.leads || r.deals || r.demos)) return <p className={`text-sm ${tc.textSecondary}`}>Sin actividad en el periodo.</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs" data-testid="economics-cohorts">
        <thead>
          <tr className={`text-left ${tc.textSecondary}`}>
            <th className="py-2 pr-2 font-medium">Mes</th>
            <th className="py-2 pr-2 text-right font-medium">Leads</th>
            <th className="py-2 pr-2 text-right font-medium">Demos</th>
            <th className="py-2 pr-2 text-right font-medium">Notas</th>
            <th className="py-2 pr-2 text-right font-medium">Negocios</th>
            <th className="py-2 pr-2 text-right font-medium">A propuesta</th>
            <th className="py-2 pr-2 text-right font-medium">Perdidos</th>
            <th className="py-2 pr-2 text-right font-medium">Ganados</th>
            <th className="py-2 pr-2 text-right font-medium">Inversión</th>
            <th className="w-32 py-2 pr-2 font-medium">Por bolsa</th>
            <th className="py-2 pr-2 text-right font-medium">Costo / lead</th>
            <th className="py-2 text-right font-medium">Costo / demo</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => (
            <tr key={c.period} className={`border-t ${tc.border}`}>
              <td className={`py-1.5 pr-2 font-medium ${tc.text}`}>{monthLabel(c.period)}</td>
              <td className={`py-1.5 pr-2 text-right tabular-nums ${tc.text}`} style={heat(c.leads, max("leads"), isDarkMode)}>{num(c.leads)}</td>
              <td className={`py-1.5 pr-2 text-right tabular-nums ${tc.text}`} style={heat(c.demos, max("demos"), isDarkMode)}>{num(c.demos)}</td>
              <td className={`py-1.5 pr-2 text-right tabular-nums ${tc.textSecondary}`}>{num(c.notes)}</td>
              <td className={`py-1.5 pr-2 text-right tabular-nums ${tc.text}`} style={heat(c.deals, max("deals"), isDarkMode)}>{num(c.deals)}</td>
              <td className={`py-1.5 pr-2 text-right tabular-nums ${tc.text}`} style={heat(c.proposals ?? 0, maxProposals, isDarkMode)}>{c.proposals == null ? "—" : num(c.proposals)}</td>
              <td className={`py-1.5 pr-2 text-right tabular-nums ${tc.text}`} style={heat(c.lost, max("lost"), isDarkMode)}>{num(c.lost)}</td>
              <td className={`py-1.5 pr-2 text-right tabular-nums ${c.won ? "font-bold text-emerald-600 dark:text-emerald-400" : tc.textSecondary}`}>{num(c.won)}</td>
              <td className={`py-1.5 pr-2 text-right tabular-nums ${tc.text}`}>{money(c.cost)}</td>
              <td className="py-1.5 pr-2">
                <BucketSplitBar amounts={c} label={`Inversión de ${monthLabel(c.period)}`} height={6} />
              </td>
              <td className={`py-1.5 pr-2 text-right tabular-nums ${tc.textSecondary}`}>{money(c.costPerLead)}</td>
              <td className={`py-1.5 text-right tabular-nums ${tc.textSecondary}`}>{money(c.costPerDemo)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PipelineTable({ report, tc }: { report: EconomicsReport; tc: TC }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs" data-testid="economics-pipelines">
        <thead>
          <tr className={`text-left ${tc.textSecondary}`}>
            <th className="py-2 pr-2 font-medium">Pipeline</th>
            <th className="py-2 pr-2 text-right font-medium">Negocios</th>
            <th className="py-2 pr-2 text-right font-medium">Sin contactar</th>
            <th className="py-2 pr-2 text-right font-medium">Activos</th>
            <th className="py-2 pr-2 text-right font-medium">En pausa</th>
            <th className="py-2 pr-2 text-right font-medium">Perdidos</th>
            <th className="py-2 pr-2 text-right font-medium">Ganados</th>
            <th className="py-2 font-medium">Estado</th>
          </tr>
        </thead>
        <tbody>
          {report.pipelines.map((p) => (
            <tr key={p.id} className={`border-t ${tc.border}`}>
              <td className={`py-2 pr-2 font-medium ${tc.text}`}>{p.label}</td>
              <td className={`py-2 pr-2 text-right tabular-nums ${tc.text}`}>{num(p.deals)}</td>
              <td className={`py-2 pr-2 text-right tabular-nums ${p.deals && p.untouched / p.deals >= 0.5 ? "font-bold text-amber-600 dark:text-amber-400" : tc.textSecondary}`}>{num(p.untouched)}</td>
              <td className={`py-2 pr-2 text-right tabular-nums ${tc.text}`}>{num(p.active)}</td>
              <td className={`py-2 pr-2 text-right tabular-nums ${tc.textSecondary}`}>{num(p.parked)}</td>
              <td className={`py-2 pr-2 text-right tabular-nums ${tc.textSecondary}`}>{num(p.lost)}</td>
              <td className={`py-2 pr-2 text-right tabular-nums ${tc.text}`}>{num(p.won)}</td>
              <td className="py-2">
                <PipelineMixBar row={p} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px]">
        {(["won", "open", "parked", "lost"] as const).map((k) => (
          <li key={k} className="flex items-center gap-1">
            <span className="inline-block h-2 w-2 rounded-sm" style={{ background: OUTCOME_META[k].color }} />
            <span className={tc.textSecondary}>{OUTCOME_META[k].label}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function BreakEven({ report, tc }: { report: EconomicsReport; tc: TC }) {
  const b = report.breakEven;
  const tile = (label: string, value: string, hint?: string) => (
    <div className={`rounded-lg border p-3 ${tc.border}`}>
      <p className={`text-[11px] ${tc.textSecondary}`}>{label}</p>
      <p className={`text-lg font-bold tabular-nums ${tc.text}`}>{value}</p>
      {hint && <p className={`text-[11px] ${tc.textSecondary}`}>{hint}</p>}
    </div>
  );
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
        {tile("Costo comercial al mes", money(b.monthlyCost))}
        {tile("Ticket promedio", money(b.avgTicket), b.ticketSamples ? `${num(b.ticketSamples)} negocios con monto` : "ningún negocio tiene monto")}
        {tile("Cierres al mes para pagarlo", b.winsPerMonth == null ? "—" : num(b.winsPerMonth), b.dealsPerMonth != null ? `se abren ${num(b.dealsPerMonth, 1)} negocios al mes` : undefined)}
        {tile("Conversión necesaria", pct(b.requiredWinRate), `hoy: ${pct(b.currentWinRate)} negocio → cierre`)}
      </div>
      {b.ticketNeeded.length > 0 && (
        <table className="w-full text-xs">
          <thead>
            <tr className={`text-left ${tc.textSecondary}`}>
              <th className="py-1.5 pr-2 font-medium">Si se cierran al mes…</th>
              {b.ticketNeeded.map((x) => (
                <th key={x.wins} className="py-1.5 pr-2 text-right font-medium">
                  {num(x.wins)} {x.wins === 1 ? "cliente" : "clientes"}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr className={`border-t ${tc.border}`}>
              <td className={`py-1.5 pr-2 ${tc.text}`}>…cada uno debe dejar al mes</td>
              {b.ticketNeeded.map((x) => (
                <td key={x.wins} className={`py-1.5 pr-2 text-right font-semibold tabular-nums ${tc.text}`}>
                  {money(x.ticket)}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Plan de acción y calidad de datos
// ---------------------------------------------------------------------------

function ActionPlan({ report, tc }: { report: EconomicsReport; tc: TC }) {
  const actionable = report.insights.filter((i) => i.severity !== "info");
  const notes = report.insights.filter((i) => i.severity === "info");
  return (
    <Exhibit kicker="Hallazgos y plan de acción" title={actionable.length ? `${num(actionable.length)} hallazgos, en orden de prioridad` : "Sin hallazgos accionables con los datos actuales"} testId="economics-insights" source="Generados por reglas sobre los datos del periodo; cada uno trae la palanca a mover.">
      <ol className="space-y-3">
        {actionable.map((i, idx) => {
          const tone = SEVERITY_TONES[i.severity];
          return (
            <li key={`${idx}-${i.title}`} className={`flex gap-3 rounded-xl border p-3 ${tc.cardBorder}`}>
              <span className={`mt-0.5 w-1 shrink-0 self-stretch rounded-full ${tone.bar}`} aria-hidden />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`text-xs font-bold tabular-nums ${tc.textSecondary}`}>{idx + 1}</span>
                  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${tone.chip}`}>
                    <i className={tone.icon} /> {tone.label}
                  </span>
                  <span className={`text-[11px] uppercase tracking-wider ${tc.textSecondary}`}>{i.area}</span>
                  {i.metric && <span className={`ml-auto text-sm font-bold tabular-nums ${tc.text}`}>{i.metric}</span>}
                </div>
                <h4 className={`mt-1 text-sm font-semibold ${tc.text}`}>{i.title}</h4>
                <p className={`mt-0.5 text-xs leading-relaxed ${tc.textSecondary}`}>{i.body}</p>
                {i.action && (
                  <p className={`mt-1.5 text-xs font-medium ${tc.text}`}>
                    <i className="ri-arrow-right-line mr-1" />
                    {i.action}
                  </p>
                )}
              </div>
            </li>
          );
        })}
      </ol>
      {notes.length > 0 && (
        <ul className={`mt-3 space-y-1 text-xs ${tc.textSecondary}`}>
          {notes.map((n, idx) => (
            <li key={`${idx}-${n.title}`}>
              <i className="ri-information-line mr-1" />
              <strong>{n.title}.</strong> {n.body}
            </li>
          ))}
        </ul>
      )}
    </Exhibit>
  );
}

function DataQuality({ report, tc }: { report: EconomicsReport; tc: TC }) {
  const q = report.dataQuality;
  const ok = q.items.filter((i) => i.status === "ok").length;
  return (
    <Exhibit kicker="Calidad de los datos" title={`${num(ok)} de ${num(q.items.length)} controles en orden: qué tan confiable es este informe`} testId="economics-quality" source="Cada control dice qué falta capturar para que el análisis sea exacto.">
      <ul className="grid grid-cols-1 gap-2 md:grid-cols-2">
        {q.items.map((i) => (
          <li key={i.id} className={`flex items-start gap-2 rounded-lg border p-2.5 text-xs ${tc.border}`}>
            <span className={`mt-1 inline-block h-2.5 w-2.5 shrink-0 rounded-full ${QUALITY_TONES[i.status].dot}`} title={QUALITY_TONES[i.status].label} />
            <div className="min-w-0">
              <p className={`font-medium ${tc.text}`}>
                {i.label} <span className={`font-normal ${tc.textSecondary}`}>· {i.value == null ? "sin dato" : pct(i.value)}</span>
              </p>
              <p className={tc.textSecondary}>{i.detail}</p>
              {i.status !== "ok" && <p className={`mt-0.5 ${tc.text}`}>{i.fix}</p>}
            </div>
          </li>
        ))}
      </ul>
    </Exhibit>
  );
}

function LeadSources({ report, tc }: { report: EconomicsReport; tc: TC }) {
  if (!report.leads.sources.length) return null;
  return (
    <Exhibit kicker="Origen de los leads" title={`${num(report.leads.fromContacts)} leads vienen de contactos de HubSpot y ${num(report.leads.fromForms)} de formularios propios`} source={report.leads.bulk ? `${num(report.leads.bulk)} entraron en carga masiva (${report.leads.bulkDays.map((b) => `${b.date}: ${num(b.count)}`).join(", ")}): una lista importada, no demanda generada.` : "Un lead es una persona; un negocio, una oportunidad de venta con una empresa."}>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className={`text-left ${tc.textSecondary}`}>
              <th className="py-2 pr-2 font-medium">Fuente</th>
              <th className="py-2 pr-2 text-right font-medium">Leads</th>
              <th className="py-2 pr-2 text-right font-medium">% del total</th>
              <th className="py-2 pr-2 text-right font-medium">Calificados</th>
              <th className="py-2 text-right font-medium">Convertidos</th>
            </tr>
          </thead>
          <tbody>
            {report.leads.sources.map((s) => (
              <tr key={s.source} className={`border-t ${tc.border}`}>
                <td className={`py-1.5 pr-2 ${tc.text}`}>{s.label}</td>
                <td className={`py-1.5 pr-2 text-right tabular-nums ${tc.text}`}>{num(s.leads)}</td>
                <td className={`py-1.5 pr-2 text-right tabular-nums ${tc.textSecondary}`}>{pct(s.share)}</td>
                <td className={`py-1.5 pr-2 text-right tabular-nums ${tc.textSecondary}`}>{num(s.qualified)}</td>
                <td className={`py-1.5 text-right tabular-nums ${tc.textSecondary}`}>{num(s.converted)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Exhibit>
  );
}

// ---------------------------------------------------------------------------
// Negocios, documentos, etapas, metodología
// ---------------------------------------------------------------------------

function DealsSection({ report, tc }: { report: EconomicsReport; tc: TC }) {
  const [showAll, setShowAll] = useState(false);
  const [filter, setFilter] = useState<"all" | StageClass>("all");
  const rows = useMemo(() => report.deals.filter((d) => filter === "all" || d.outcome === filter), [report.deals, filter]);
  const visible = showAll ? rows : rows.slice(0, 15);
  const docsById = useMemo(() => Object.fromEntries(report.documents.map((d) => [d.id, d])), [report.documents]);
  return (
    <Exhibit
      kicker="Detalle"
      title={`Negocios del periodo (${num(report.deals.length)})`}
      source="Activos primero. «Días en etapa» con el recorrido real cuando existe; en ámbar los que llevan más de 60 días sin moverse."
      aside={
        <div className={`inline-flex rounded-lg border p-0.5 text-xs ${tc.border}`} role="group" aria-label="Filtrar negocios">
          {(["all", "open", "parked", "lost", "won"] as const).map((f) => (
            <button key={f} type="button" aria-pressed={filter === f} onClick={() => setFilter(f)} className={`rounded-md px-2 py-1 ${filter === f ? "bg-primary-500 text-white" : tc.buttonText}`}>
              {f === "all" ? "Todos" : OUTCOME_META[f].label}
            </button>
          ))}
        </div>
      }
    >
      {rows.length === 0 ? (
        <p className={`text-sm ${tc.textSecondary}`}>Sin negocios con ese filtro.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="economics-deals">
            <thead>
              <tr className={`text-left ${tc.textSecondary}`}>
                <th className="py-2 pr-2 font-medium">Negocio</th>
                <th className="py-2 pr-2 font-medium">Empresa</th>
                <th className="py-2 pr-2 font-medium">Pipeline</th>
                <th className="py-2 pr-2 font-medium">Etapa</th>
                <th className="py-2 pr-2 text-right font-medium">Monto</th>
                <th className="py-2 pr-2 text-right font-medium">Días abierto</th>
                <th className="py-2 pr-2 text-right font-medium">Días en etapa</th>
                <th className="py-2 font-medium">Documentos</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((d) => (
                <tr key={d.id} className={`border-t ${tc.border}`}>
                  <td className={`py-2 pr-2 font-medium ${tc.text}`}>
                    {d.title}
                    {!d.exactHistory && <span className={`ml-1 text-[10px] ${tc.textSecondary}`} title="Sin recorrido de etapas: estimado">≈</span>}
                  </td>
                  <td className={`py-2 pr-2 ${tc.textSecondary}`}>{d.company || "—"}</td>
                  <td className={`py-2 pr-2 ${tc.textSecondary}`}>{d.pipelineLabel || "—"}</td>
                  <td className="py-2 pr-2">
                    <span className={`rounded-full px-2 py-0.5 text-[11px] ${OUTCOME_META[d.outcome].chip}`}>{d.stageLabel}</span>
                  </td>
                  <td className={`py-2 pr-2 text-right tabular-nums ${d.amount ? tc.text : tc.textSecondary}`}>{d.amount ? money(d.amount) : "sin monto"}</td>
                  <td className={`py-2 pr-2 text-right tabular-nums ${tc.textSecondary}`}>{d.daysOpen == null ? "—" : num(d.daysOpen)}</td>
                  <td className={`py-2 pr-2 text-right tabular-nums ${d.outcome === "open" && d.daysInStage != null && d.daysInStage > 60 ? "font-bold text-amber-600 dark:text-amber-400" : tc.textSecondary}`}>{d.outcome === "open" && d.daysInStage != null ? num(d.daysInStage) : "—"}</td>
                  <td className="py-2">
                    {d.documents === 0 ? (
                      <span className={tc.textSecondary}>—</span>
                    ) : (
                      <span className="flex flex-wrap gap-1">
                        {d.documentIds.slice(0, 2).map((id) => {
                          const doc = docsById[id];
                          return doc?.url ? (
                            <a key={id} href={doc.url} target="_blank" rel="noreferrer" className="text-primary-500 hover:underline">
                              <i className={doc.isPdf ? "ri-file-pdf-2-line" : "ri-attachment-2"} /> {doc.name}
                            </a>
                          ) : (
                            <span key={id} className={tc.textSecondary}>
                              <i className="ri-attachment-2" /> {doc?.name ?? id}
                            </span>
                          );
                        })}
                        {d.documents > 2 && <span className={tc.textSecondary}>+{d.documents - 2}</span>}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length > 15 && (
            <button type="button" onClick={() => setShowAll((v) => !v)} className={`mt-2 text-xs ${tc.buttonText} hover:underline`}>
              {showAll ? "Ver menos" : `Ver los ${num(rows.length)} negocios`}
            </button>
          )}
        </div>
      )}
    </Exhibit>
  );
}

function DocumentsSection({ report, tc }: { report: EconomicsReport; tc: TC }) {
  const [onlyPdf, setOnlyPdf] = useState(false);
  const docs = report.documents.filter((d) => !onlyPdf || d.isPdf);
  const err = report.hubspot.errors?.files;
  const s = report.documentsSummary;
  return (
    <Exhibit
      kicker="Detalle"
      title={`Documentos en HubSpot: ${num(s.total)} adjuntos, ${num(s.linkedToDeals)} ligados a negocios`}
      source="Archivos del portal y adjuntos de notas (propuestas, cotizaciones), con el negocio, empresa o contacto al que están ligados."
      aside={
        <label className={`flex items-center gap-1 text-xs ${tc.textSecondary}`}>
          <input type="checkbox" checked={onlyPdf} onChange={(e) => setOnlyPdf(e.target.checked)} /> Solo PDF
        </label>
      }
    >
      {err && (
        <p className="mb-2 rounded-lg bg-amber-500/10 p-2 text-xs text-amber-700 dark:text-amber-400">
          La app de HubSpot no tiene el permiso <code>files</code>: se ven los {num(s.withoutFile)} adjuntos referenciados en notas, pero no su nombre ni su enlace. Agrega el permiso y pulsa «Actualizar HubSpot».
        </p>
      )}
      {docs.length === 0 ? (
        <p className={`text-sm ${tc.textSecondary}`}>{report.hubspot.connected ? "Sin documentos con ese filtro." : "Conecta HubSpot para ver sus documentos."}</p>
      ) : (
        <ul className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3" data-testid="economics-documents">
          {docs.slice(0, 60).map((d) => (
            <li key={d.id} className={`flex items-start gap-2 rounded-lg border p-2 text-xs ${tc.border}`}>
              <i className={`${d.isPdf ? "ri-file-pdf-2-line text-red-500" : "ri-file-3-line text-primary-500"} mt-0.5 text-base`} />
              <div className="min-w-0 flex-1">
                {d.url ? (
                  <a href={d.url} target="_blank" rel="noreferrer" className={`block truncate font-medium ${tc.text} hover:underline`}>
                    {d.name}
                  </a>
                ) : (
                  <p className={`truncate font-medium ${tc.text}`}>{d.name}</p>
                )}
                <p className={`truncate ${tc.textSecondary}`}>{[d.extension?.toUpperCase(), d.size != null ? fileSize(d.size) : null, d.createdAt ? new Date(d.createdAt).toLocaleDateString("es-MX") : null].filter(Boolean).join(" · ") || "adjunto de una nota"}</p>
                {d.linked.length > 0 && (
                  <p className={`truncate ${tc.textSecondary}`}>
                    <i className="ri-link mr-1" />
                    {d.linked.map((l) => l.name || (l.type === "deal" ? "negocio" : l.type === "company" ? "empresa" : "contacto")).join(", ")}
                  </p>
                )}
                {d.note && <p className={`truncate italic ${tc.textSecondary}`}>{d.note}</p>}
              </div>
            </li>
          ))}
        </ul>
      )}
      {docs.length > 60 && <p className={`mt-2 text-xs ${tc.textSecondary}`}>Se muestran 60 de {num(docs.length)}.</p>}
    </Exhibit>
  );
}

function StageDefinitions({ report, tc, onChange, saving }: { report: EconomicsReport; tc: TC; onChange: (stageId: string, kind: StageClass | null) => void; saving: string | null }) {
  const [open, setOpen] = useState(false);
  const { isDarkMode } = useTheme();
  if (!report.stageDefinitions.length) return null;
  const select = `rounded-lg border px-2 py-1 text-xs ${isDarkMode ? "bg-[#232323] border-dark-border text-white" : "bg-white border-gray-300 text-gray-900"}`;
  return (
    <div className={`rounded-2xl border p-4 ${tc.cardBorder} ${tc.cardBg}`} data-testid="economics-stages">
      <button type="button" onClick={() => setOpen((v) => !v)} className={`flex w-full items-center justify-between text-left text-sm font-bold ${tc.text}`} aria-expanded={open}>
        Definición de etapas: qué cuenta como ganada, perdida o en pausa
        <i className={open ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />
      </button>
      {open && (
        <div className="mt-3 overflow-x-auto">
          <p className={`mb-2 text-xs ${tc.textSecondary}`}>Cada etapa se clasifica por su nombre; la configuración de HubSpot solo decide cuando el nombre no lo dice. Si una clasificación es incorrecta, fíjala aquí: se guarda para todos.</p>
          <table className="w-full text-xs">
            <thead>
              <tr className={`text-left ${tc.textSecondary}`}>
                <th className="py-1.5 pr-2 font-medium">Pipeline</th>
                <th className="py-1.5 pr-2 font-medium">Etapa</th>
                <th className="py-1.5 pr-2 text-right font-medium">Probabilidad en HubSpot</th>
                <th className="py-1.5 pr-2 font-medium">Se cuenta como</th>
                <th className="py-1.5 font-medium" />
              </tr>
            </thead>
            <tbody>
              {report.stageDefinitions.map((s) => (
                <tr key={s.id} className={`border-t ${tc.border}`}>
                  <td className={`py-1.5 pr-2 ${tc.textSecondary}`}>{s.pipelineLabel || "—"}</td>
                  <td className={`py-1.5 pr-2 font-medium ${tc.text}`}>{s.label}</td>
                  <td className={`py-1.5 pr-2 text-right tabular-nums ${tc.textSecondary}`}>{pct(s.probability)}</td>
                  <td className="py-1.5 pr-2">
                    <select aria-label={`Clasificación de ${s.label}`} className={select} value={s.kind} disabled={saving != null} aria-busy={saving === s.id} onChange={(e) => onChange(s.id, e.target.value as StageClass)}>
                      {STAGE_CLASS_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="py-1.5">
                    {s.overridden && (
                      <button type="button" disabled={saving != null} onClick={() => onChange(s.id, null)} className={`text-[11px] ${tc.buttonText} hover:underline disabled:opacity-50`}>
                        Volver a automática
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Methodology({ report, tc }: { report: EconomicsReport; tc: TC }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`rounded-2xl border p-4 ${tc.cardBorder} ${tc.cardBg}`}>
      <button type="button" onClick={() => setOpen((v) => !v)} className={`flex w-full items-center justify-between text-left text-sm font-bold ${tc.text}`} aria-expanded={open}>
        Metodología y supuestos
        <i className={open ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />
      </button>
      {open && (
        <ol className={`mt-2 list-decimal space-y-1 pl-5 text-xs leading-relaxed ${tc.textSecondary}`}>
          {report.assumptions.map((a) => (
            <li key={a}>{a}</li>
          ))}
          <li>
            Generado {relativeTime(report.generatedAt)} con {num(report.costs.expensesCount)} gastos y {num(report.costs.team.length)} personas del equipo comercial.
          </li>
        </ol>
      )}
    </div>
  );
}
